import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/auth.store';

export function DemoTimer() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!user?.demoExpiresAt) return;

    const update = () => {
      const now = new Date().getTime();
      const expires = new Date(user.demoExpiresAt!).getTime();
      const diff = expires - now;

      if (diff <= 0) {
        setExpired(true);
        setTimeLeft('0:00');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft(
        hours > 0
          ? `${hours}h ${minutes}m`
          : `${minutes}:${seconds.toString().padStart(2, '0')}`
      );
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [user?.demoExpiresAt]);

  // Don't show if user has a paid subscription or no demo
  if (!user?.demoExpiresAt || (user.subscriptionTier && user.subscriptionTier !== 'free')) {
    return null;
  }

  if (expired) {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-red-900/95 border border-red-500 rounded-xl p-4 shadow-2xl z-50 backdrop-blur">
        <div className="text-red-200 font-semibold mb-1">{t('demo.expired')}</div>
        <p className="text-red-300 text-sm mb-3">{t('demo.expiredMessage')}</p>
        <a
          href="/plans"
          className="block text-center py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white font-medium transition-colors"
        >
          {t('demo.subscribe')}
        </a>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 bg-slate-900/95 border border-blue-500/50 rounded-xl p-3 shadow-2xl z-50 backdrop-blur flex items-center gap-3">
      <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
        <span className="text-blue-400 text-lg">⏱</span>
      </div>
      <div>
        <div className="text-xs text-slate-400">{t('demo.timeRemaining')}</div>
        <div className="text-lg font-mono font-bold text-blue-400">{timeLeft}</div>
      </div>
    </div>
  );
}
